import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import {
  Dumbbell,
  Filter,
  Calendar,
  Clock,
  Zap,
  AlertCircle,
  RefreshCw,
  Sparkles,
  Play,
  X,
  Flame,
  TrendingUp,
  Activity,
  ChevronRight,
  Award,
  BarChart2
} from 'lucide-react';
import PageHeader from '../components/PageHeader';
import AIConsentModal, { hasAIConsent } from '../components/AIConsentModal';
import { ExerciseProgressModal } from '../components/ExerciseProgressModal';
import InsufficientCreditsModal from '../components/InsufficientCreditsModal';
import YouTubePlayer from '../components/YouTubePlayer';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3000';

interface Exercise {
  title: string;
  youtube_url: string;
  difficulty: string;
  location: string;
  duration: number | string;
  benefits: string;
  contraindications?: string;
  muscle_groups?: string;
  workout_type?: string;
  calories_burned?: number;
}

const GENERATING_KEY = 'exercises_generating';

// Fitness goal config
const FITNESS_GOAL_CONFIG: Record<string, {
  emoji: string;
  title: string;
  subtitle: string;
  tags: string[];
  color: string;
  bgGradient: string;
}> = {
  WEIGHT_LOSS: {
    emoji: '🔥',
    title: 'Antrenament pentru Slăbit',
    subtitle: 'Cardio HIIT + exerciții dinamice pentru ardere calorică maximă',
    tags: ['🔥 Ardere calorii', '⚡ HIIT 15-25min', '💪 Cardio intensiv'],
    color: 'from-orange-500 to-red-500',
    bgGradient: 'from-orange-500 to-red-500'
  },
  MUSCLE_GAIN: {
    emoji: '💪',
    title: 'Antrenament pentru Masă Musculară',
    subtitle: 'Strength training progresiv cu focus pe hipertrofie musculară',
    tags: ['💪 Strength focused', '⏱️ 30-45min', '🎯 Hipertrofie'],
    color: 'from-blue-600 to-indigo-600',
    bgGradient: 'from-blue-600 to-indigo-600'
  },
  HEALTHY_LIFESTYLE: {
    emoji: '🌟',
    title: 'Fitness & Lifestyle Activ',
    subtitle: 'Mix echilibrat: cardio, forță, flexibilitate pentru wellness complet',
    tags: ['⚖️ Echilibrat', '🏃 Cardio + Forță', '✨ Wellness'],
    color: 'from-green-500 to-teal-500',
    bgGradient: 'from-green-500 to-teal-500'
  },
  REDUCE_BELLY_FAT: {
    emoji: '🎯',
    title: 'Reducere Grăsime Abdominală',
    subtitle: 'Core training intensiv + HIIT pentru ardere grăsime targeted',
    tags: ['🎯 Core training', '🔥 Fat burn', '⚡ HIIT'],
    color: 'from-red-500 to-pink-500',
    bgGradient: 'from-red-500 to-pink-500'
  },
  INCREASE_ENERGY: {
    emoji: '⚡',
    title: 'Energie & Vitalitate',
    subtitle: 'Exerciții moderate pentru boost metabolism și vitalitate zilnică',
    tags: ['⚡ Energizant', '🌅 Dimineața', '💫 Vitalitate'],
    color: 'from-yellow-500 to-orange-500',
    bgGradient: 'from-yellow-500 to-orange-500'
  },
  START_EXERCISE: {
    emoji: '🚀',
    title: 'Primul Pas în Fitness',
    subtitle: 'Exerciții pentru începători: pas cu pas, fără presiune, la ritmul tău',
    tags: ['🚀 Beginner friendly', '⏱️ 10-20min', '📈 Progresiv'],
    color: 'from-purple-500 to-violet-500',
    bgGradient: 'from-purple-500 to-violet-500'
  },
  MAINTAIN_WEIGHT: {
    emoji: '⚖️',
    title: 'Menținere & Tonifiere',
    subtitle: 'Rutină echilibrată pentru consistență și menținere formei fizice',
    tags: ['⚖️ Echilibru', '🏋️ Tonifiere', '📅 Consistență'],
    color: 'from-teal-500 to-cyan-500',
    bgGradient: 'from-teal-500 to-cyan-500'
  },
  IMPROVE_BALANCE: {
    emoji: '🧘',
    title: 'Echilibru & Coordonare',
    subtitle: 'Training echilibru, propriocepție și coordonare motorie',
    tags: ['🧘 Echilibru', '🎯 Coordonare', '⏱️ 15-20min'],
    color: 'from-indigo-500 to-purple-500',
    bgGradient: 'from-indigo-500 to-purple-500'
  }
};

const DEFAULT_GOAL_CONFIG = {
  emoji: '🎯',
  title: 'Antrenament Personalizat',
  subtitle: 'Exerciții generate cu AI, adaptate perfect obiectivului tău fitness',
  tags: ['🎯 Personalizat', '⏱️ Adaptat', '✨ AI-curated'],
  color: 'from-purple-600 to-blue-600',
  bgGradient: 'from-purple-600 to-blue-600'
};

// Fitness location labels
const LOCATION_CONFIG: Record<string, { label: string; icon: string; color: string }> = {
  'sala': { label: 'Sală Fitness', icon: '🏋️', color: 'bg-blue-100 text-blue-700' },
  'acasa': { label: 'Acasă', icon: '🏠', color: 'bg-green-100 text-green-700' },
  'exterior': { label: 'Exterior', icon: '🌳', color: 'bg-emerald-100 text-emerald-700' },
  'yoga_mat': { label: 'Yoga Mat', icon: '🧘', color: 'bg-purple-100 text-purple-700' },
  'banda_elastica': { label: 'Bandă Elastică', icon: '🎗️', color: 'bg-orange-100 text-orange-700' },
  'greutati': { label: 'Greutăți', icon: '🏋️', color: 'bg-red-100 text-red-700' },
  // Legacy support
  'pat': { label: 'Acasă', icon: '🏠', color: 'bg-green-100 text-green-700' },
  'scaun': { label: 'Acasă', icon: '🏠', color: 'bg-green-100 text-green-700' },
  'perete': { label: 'Acasă', icon: '🏠', color: 'bg-green-100 text-green-700' },
};

export default function ExercisesPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [showProgress, setShowProgress] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [showAIConsent, setShowAIConsent] = useState(!hasAIConsent());
  const [exercises, setExercises] = useState<Exercise[]>([]);
  const [dailyExercises, setDailyExercises] = useState<Exercise[]>([]);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState<'all' | 'daily'>('daily');

  // Filters
  const [difficultyFilter, setDifficultyFilter] = useState<string>('toate');
  const [locationFilter, setLocationFilter] = useState<string>('toate');
  const [workoutTypeFilter, setWorkoutTypeFilter] = useState<string>('toate');

  // Video modal
  const [selectedVideo, setSelectedVideo] = useState<Exercise | null>(null);

  // Insufficient credits modal
  const [creditsModal, setCreditsModal] = useState<{ open: boolean; available: number; required: number }>({
    open: false, available: 0, required: 0
  });
  const showCreditsModal = (available: number, required: number) => {
    setCreditsModal({ open: true, available, required });
  };

  useEffect(() => {
    const wasGenerating = localStorage.getItem(GENERATING_KEY) === 'true';
    if (wasGenerating) {
      checkExistingExercisesOnReturn(wasGenerating);
    } else {
      checkExistingExercises();
    }
  }, []);

  const checkExistingExercisesOnReturn = async (wasGenerating: boolean) => {
    try {
      const token = localStorage.getItem('accessToken');
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 3000);

      const response = await axios.get(`${API_URL}/api/exercises`, {
        headers: { Authorization: `Bearer ${token}` },
        signal: controller.signal,
        timeout: 3000
      });

      clearTimeout(timeoutId);

      if (response.data.exercises && response.data.exercises.length > 0) {
        localStorage.removeItem(GENERATING_KEY);
        setExercises(response.data.exercises);
        loadDailyRecommendations();
      } else if (wasGenerating) {
        setGenerating(true);
        setShowProgress(true);
      }
      setError('');
    } catch (error: any) {
      if (error.code === 'ECONNABORTED' || error.name === 'AbortError' || error.code === 'ERR_CANCELED') {
        if (wasGenerating) {
          setGenerating(true);
          setShowProgress(true);
        }
      } else {
        console.error('Error checking exercises on return:', error);
        if (wasGenerating) {
          setGenerating(true);
          setShowProgress(true);
        }
      }
    } finally {
      setLoading(false);
    }
  };

  const checkExistingExercises = async () => {
    try {
      const token = localStorage.getItem('accessToken');
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 2000);

      const response = await axios.get(`${API_URL}/api/exercises`, {
        headers: { Authorization: `Bearer ${token}` },
        signal: controller.signal,
        timeout: 2000
      });

      clearTimeout(timeoutId);

      if (response.data.exercises && response.data.exercises.length > 0) {
        setExercises(response.data.exercises);
        loadDailyRecommendations();
      }
      setError('');
    } catch (error: any) {
      if (error.code === 'ECONNABORTED' || error.name === 'AbortError' || error.code === 'ERR_CANCELED') {
        console.log('⚡ Timeout - afișare instant empty state');
      } else {
        console.error('Error checking exercises:', error);
      }
    } finally {
      setLoading(false);
    }
  };

  const generateExercises = async () => {
    setGenerating(true);
    setShowProgress(true);
    localStorage.setItem(GENERATING_KEY, 'true');

    try {
      const token = localStorage.getItem('accessToken');

      axios.post(
        `${API_URL}/api/exercises/generate`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      ).then(() => {
        console.log('Exercise generation started');
      }).catch(error => {
        console.error('Error starting generation:', error);
        localStorage.removeItem(GENERATING_KEY);
        setShowProgress(false);
        setGenerating(false);

        if (error.response?.status === 402 || error.response?.data?.code === 'INSUFFICIENT_CREDITS') {
          const available = error.response?.data?.available ?? 0;
          const required = error.response?.data?.required ?? 0;
          showCreditsModal(available, required);
        } else {
          const errorMsg = error.response?.data?.error || 'Eroare la generarea antrenamentului';
          setError(errorMsg);
        }
      });
    } catch (error: any) {
      console.error('Error in generateExercises:', error);
      localStorage.removeItem(GENERATING_KEY);
      setShowProgress(false);
      setGenerating(false);

      if (error.response?.status === 402 || error.response?.data?.code === 'INSUFFICIENT_CREDITS') {
        const available = error.response?.data?.available ?? 0;
        const required = error.response?.data?.required ?? 0;
        showCreditsModal(available, required);
      } else {
        setError('Eroare la generarea antrenamentului. Verifică dacă ai profilul completat.');
      }
    }
  };

  const handleProgressComplete = async () => {
    localStorage.removeItem(GENERATING_KEY);
    setShowProgress(false);
    setGenerating(false);
    await checkExistingExercises();
  };

  const handleProgressError = (error: string) => {
    localStorage.removeItem(GENERATING_KEY);
    setShowProgress(false);
    setGenerating(false);
    if (error.toLowerCase().includes('credite') || error.toLowerCase().includes('credit')) {
      const match = error.match(/(\d+)\s*credite disponibile.*?(\d+)/);
      if (match) {
        showCreditsModal(parseInt(match[1]), parseInt(match[2]));
        return;
      }
    }
    setError(error);
  };

  const loadDailyRecommendations = async () => {
    try {
      const token = localStorage.getItem('accessToken');
      const response = await axios.get(`${API_URL}/api/exercises/daily`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDailyExercises(response.data.exercises || []);
    } catch (error) {
      console.error('Error loading daily exercises:', error);
    }
  };

  const refreshExercises = async () => {
    setRefreshing(true);
    try {
      const token = localStorage.getItem('accessToken');
      const response = await axios.post(
        `${API_URL}/api/exercises/refresh`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setExercises(response.data.exercises || []);
    } catch (error: any) {
      console.error('Error refreshing exercises:', error);
      if (error.response?.status === 402 || error.response?.data?.code === 'INSUFFICIENT_CREDITS') {
        const available = error.response?.data?.available ?? 0;
        const required = error.response?.data?.required ?? 0;
        showCreditsModal(available, required);
      } else {
        setError(error.response?.data?.error || 'Eroare la regenerare');
      }
    } finally {
      setRefreshing(false);
    }
  };

  const getYouTubeId = (url: string) => {
    const match = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/)([^&\s]+)/);
    return match ? match[1] : '';
  };

  const filteredExercises = exercises.filter(ex => {
    const diffMatch = difficultyFilter === 'toate' || ex.difficulty === difficultyFilter;
    const locMatch = locationFilter === 'toate' || ex.location === locationFilter;
    const typeMatch = workoutTypeFilter === 'toate' || ex.workout_type === workoutTypeFilter;
    return diffMatch && locMatch && typeMatch;
  });

  const getDifficultyConfig = (difficulty: string) => {
    switch (difficulty) {
      case 'ușor': return { class: 'bg-green-100 text-green-700 border-green-200', label: '🟢 Ușor' };
      case 'mediu': return { class: 'bg-yellow-100 text-yellow-700 border-yellow-200', label: '🟡 Mediu' };
      case 'avansat': return { class: 'bg-red-100 text-red-700 border-red-200', label: '🔴 Avansat' };
      default: return { class: 'bg-gray-100 text-gray-700 border-gray-200', label: difficulty };
    }
  };

  const getLocationConfig = (location: string) => {
    return LOCATION_CONFIG[location] || { label: location, icon: '📍', color: 'bg-gray-100 text-gray-700' };
  };

  const goalConfig = user?.healthGoal
    ? (FITNESS_GOAL_CONFIG[user.healthGoal] || DEFAULT_GOAL_CONFIG)
    : DEFAULT_GOAL_CONFIG;

  // Stats
  const totalDuration = exercises.reduce((sum, ex) => {
    const dur = typeof ex.duration === 'string'
      ? parseInt(ex.duration.split('-')[0]) || 10
      : (ex.duration || 10);
    return sum + dur;
  }, 0);

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-orange-50 via-white to-red-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-orange-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600 font-medium">Se încarcă antrenamentele...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-orange-50">
      <PageHeader
        title="Antrenamente Fitness"
        subtitle="Exerciții personalizate cu AI pentru obiectivul tău"
        showBack={true}
        backTo="/dashboard"
        icon={<Dumbbell className="w-5 h-5" />}
      />

      <div className="max-w-7xl mx-auto px-4 py-6">

        {/* Error */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-2xl p-5 mb-6">
            <div className="flex items-center gap-3">
              <AlertCircle className="w-6 h-6 text-red-600 flex-shrink-0" />
              <div>
                <p className="font-semibold text-red-900">Eroare</p>
                <p className="text-sm text-red-700">{error}</p>
                <p className="text-xs text-red-600 mt-1">
                  Asigură-te că ai setat obiectivul fitness în profil.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* FITNESS GOAL HERO BANNER */}
        {user?.healthGoal && (
          <div className={`bg-gradient-to-br ${goalConfig.bgGradient} rounded-3xl p-6 mb-6 text-white shadow-2xl relative overflow-hidden`}>
            {/* Background decoration */}
            <div className="absolute top-0 right-0 w-40 h-40 bg-white/10 rounded-full -translate-y-10 translate-x-10"></div>
            <div className="absolute bottom-0 left-0 w-32 h-32 bg-white/10 rounded-full translate-y-10 -translate-x-10"></div>

            <div className="relative flex items-start gap-4">
              <div className="p-3 bg-white/20 rounded-2xl backdrop-blur-sm flex-shrink-0">
                <span className="text-3xl">{goalConfig.emoji}</span>
              </div>
              <div className="flex-1">
                <h3 className="text-xl font-bold mb-1">{goalConfig.title}</h3>
                <p className="text-sm text-white/85 mb-4">{goalConfig.subtitle}</p>
                <div className="flex flex-wrap gap-2">
                  {goalConfig.tags.map((tag, i) => (
                    <span key={i} className="text-xs bg-white/20 px-3 py-1.5 rounded-full backdrop-blur-sm font-medium">
                      {tag}
                    </span>
                  ))}
                </div>
              </div>
            </div>

            {/* Stats row */}
            {exercises.length > 0 && (
              <div className="relative mt-5 pt-4 border-t border-white/20 grid grid-cols-3 gap-4">
                <div className="text-center">
                  <p className="text-2xl font-bold">{exercises.length}</p>
                  <p className="text-xs text-white/75">Exerciții</p>
                </div>
                <div className="text-center">
                  <p className="text-2xl font-bold">{totalDuration}+</p>
                  <p className="text-xs text-white/75">Minute total</p>
                </div>
                <div className="text-center">
                  <p className="text-2xl font-bold">{dailyExercises.length}</p>
                  <p className="text-xs text-white/75">Azi recomandate</p>
                </div>
              </div>
            )}
          </div>
        )}

        {exercises.length > 0 && (
          <>
            {/* Action Bar */}
            <div className="bg-white rounded-2xl shadow-lg p-4 mb-6 border border-gray-100">
              <div className="flex items-center justify-between flex-wrap gap-3">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-orange-100 rounded-xl">
                    <Sparkles className="w-5 h-5 text-orange-600" />
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-gray-900">Antrenamente generate cu AI Fitness</p>
                    <p className="text-xs text-gray-500">Personalizate pentru obiectivul tău • Actualizate săptămânal</p>
                  </div>
                </div>
                <button
                  onClick={refreshExercises}
                  disabled={refreshing}
                  className="flex items-center gap-2 px-4 py-2.5 bg-gradient-to-r from-orange-500 to-red-500 text-white rounded-xl hover:shadow-lg transition-all disabled:opacity-50 font-medium text-sm"
                  title="Regenerare disponibilă la 7 zile"
                >
                  <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
                  <span>{refreshing ? 'Regenerez...' : 'Regenerează'}</span>
                </button>
              </div>
            </div>

            {/* Tabs */}
            <div className="flex gap-2 mb-6">
              <button
                onClick={() => setActiveTab('daily')}
                className={`flex-1 py-3 px-4 rounded-xl font-semibold text-sm transition-all flex items-center justify-center gap-2 ${
                  activeTab === 'daily'
                    ? 'bg-gradient-to-r from-orange-500 to-red-500 text-white shadow-lg'
                    : 'bg-white text-gray-600 border border-gray-200 hover:border-orange-300'
                }`}
              >
                <Calendar className="w-4 h-4" />
                Antrenament Azi
                {dailyExercises.length > 0 && (
                  <span className={`text-xs px-2 py-0.5 rounded-full ${activeTab === 'daily' ? 'bg-white/20' : 'bg-orange-100 text-orange-700'}`}>
                    {dailyExercises.length}
                  </span>
                )}
              </button>
              <button
                onClick={() => setActiveTab('all')}
                className={`flex-1 py-3 px-4 rounded-xl font-semibold text-sm transition-all flex items-center justify-center gap-2 ${
                  activeTab === 'all'
                    ? 'bg-gradient-to-r from-orange-500 to-red-500 text-white shadow-lg'
                    : 'bg-white text-gray-600 border border-gray-200 hover:border-orange-300'
                }`}
              >
                <Dumbbell className="w-4 h-4" />
                Toate Exercițiile
                <span className={`text-xs px-2 py-0.5 rounded-full ${activeTab === 'all' ? 'bg-white/20' : 'bg-gray-100 text-gray-600'}`}>
                  {exercises.length}
                </span>
              </button>
            </div>

            {/* DAILY TAB */}
            {activeTab === 'daily' && (
              <div>
                {dailyExercises.length > 0 ? (
                  <>
                    {/* Today's workout header */}
                    <div className="bg-gradient-to-r from-orange-50 to-red-50 rounded-2xl p-5 mb-5 border border-orange-100">
                      <div className="flex items-center gap-3 mb-1">
                        <div className="p-2 bg-orange-500 rounded-xl">
                          <Flame className="w-5 h-5 text-white" />
                        </div>
                        <div>
                          <h3 className="font-bold text-gray-900 text-lg">Antrenamentul de Azi</h3>
                          <p className="text-sm text-gray-600">
                            {dailyExercises.length} exerciții • ~{dailyExercises.reduce((sum, ex) => {
                              const dur = typeof ex.duration === 'string'
                                ? parseInt(ex.duration.split('-')[0]) || 10
                                : (ex.duration || 10);
                              return sum + dur;
                            }, 0)} minute
                          </p>
                        </div>
                      </div>
                    </div>

                    <div className="space-y-4">
                      {dailyExercises.map((ex, idx) => (
                        <div
                          key={idx}
                          className="bg-white rounded-2xl shadow-md border border-gray-100 overflow-hidden hover:shadow-xl transition-all cursor-pointer group"
                          onClick={() => setSelectedVideo(ex)}
                        >
                          <div className="flex gap-0">
                            {/* Thumbnail */}
                            <div className="relative w-36 sm:w-48 flex-shrink-0">
                              <img
                                src={`https://img.youtube.com/vi/${getYouTubeId(ex.youtube_url)}/mqdefault.jpg`}
                                alt={ex.title}
                                className="w-full h-full object-cover"
                              />
                              <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-all flex items-center justify-center">
                                <div className="w-10 h-10 bg-white/90 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all shadow-lg">
                                  <Play className="w-5 h-5 text-orange-600 ml-0.5" />
                                </div>
                              </div>
                              {/* Step number */}
                              <div className="absolute top-2 left-2 w-7 h-7 bg-orange-500 rounded-full flex items-center justify-center text-white text-xs font-bold shadow">
                                {idx + 1}
                              </div>
                            </div>

                            {/* Info */}
                            <div className="flex-1 p-4">
                              <h4 className="font-semibold text-gray-900 mb-2 line-clamp-2 group-hover:text-orange-600 transition-colors">
                                {ex.title}
                              </h4>

                              <div className="flex flex-wrap items-center gap-2 mb-2">
                                <span className={`text-xs px-2 py-1 rounded-full border font-medium ${getDifficultyConfig(ex.difficulty).class}`}>
                                  {getDifficultyConfig(ex.difficulty).label}
                                </span>
                                <span className={`text-xs px-2 py-1 rounded-full font-medium ${getLocationConfig(ex.location).color}`}>
                                  {getLocationConfig(ex.location).icon} {getLocationConfig(ex.location).label}
                                </span>
                                <span className="text-xs text-gray-500 flex items-center gap-1">
                                  <Clock className="w-3 h-3" />
                                  {ex.duration} min
                                </span>
                              </div>

                              <p className="text-xs text-gray-600 line-clamp-2 flex items-start gap-1">
                                <Zap className="w-3 h-3 text-orange-500 flex-shrink-0 mt-0.5" />
                                {ex.benefits}
                              </p>
                            </div>

                            <div className="flex items-center pr-3">
                              <ChevronRight className="w-5 h-5 text-gray-400 group-hover:text-orange-500 transition-colors" />
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>

                    {/* Workout complete CTA */}
                    <div className="mt-6 bg-gradient-to-r from-green-50 to-emerald-50 rounded-2xl p-5 border border-green-200 text-center">
                      <Award className="w-8 h-8 text-green-600 mx-auto mb-2" />
                      <p className="font-semibold text-green-900 mb-1">Completează antrenamentul de azi!</p>
                      <p className="text-sm text-green-700">Consistența este cheia succesului în fitness 💪</p>
                    </div>
                  </>
                ) : (
                  <div className="text-center py-12 bg-white rounded-2xl shadow-md">
                    <Calendar className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                    <p className="text-gray-500">Nu există recomandări pentru azi</p>
                  </div>
                )}
              </div>
            )}

            {/* ALL EXERCISES TAB */}
            {activeTab === 'all' && (
              <div>
                {/* Filters */}
                <div className="bg-white rounded-2xl shadow-md p-5 mb-6 border border-gray-100">
                  <div className="flex items-center gap-2 mb-4">
                    <Filter className="w-4 h-4 text-gray-600" />
                    <h3 className="font-semibold text-gray-900">Filtrează Exercițiile</h3>
                  </div>

                  <div className="space-y-4">
                    {/* Difficulty */}
                    <div>
                      <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
                        Nivel Dificultate
                      </label>
                      <div className="flex flex-wrap gap-2">
                        {[
                          { value: 'toate', label: 'Toate' },
                          { value: 'ușor', label: '🟢 Ușor' },
                          { value: 'mediu', label: '🟡 Mediu' },
                          { value: 'avansat', label: '🔴 Avansat' }
                        ].map(level => (
                          <button
                            key={level.value}
                            onClick={() => setDifficultyFilter(level.value)}
                            className={`px-4 py-2 rounded-xl border-2 transition-all text-sm font-medium ${
                              difficultyFilter === level.value
                                ? 'border-orange-500 bg-orange-50 text-orange-700'
                                : 'border-gray-200 hover:border-gray-300 text-gray-600'
                            }`}
                          >
                            {level.label}
                          </button>
                        ))}
                      </div>
                    </div>

                    {/* Location */}
                    <div>
                      <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
                        Locație / Echipament
                      </label>
                      <div className="flex flex-wrap gap-2">
                        {[
                          { value: 'toate', label: '📋 Toate' },
                          { value: 'sala', label: '🏋️ Sală' },
                          { value: 'acasa', label: '🏠 Acasă' },
                          { value: 'exterior', label: '🌳 Exterior' },
                          { value: 'yoga_mat', label: '🧘 Yoga Mat' },
                          { value: 'banda_elastica', label: '🎗️ Bandă' }
                        ].map(loc => (
                          <button
                            key={loc.value}
                            onClick={() => setLocationFilter(loc.value)}
                            className={`px-4 py-2 rounded-xl border-2 transition-all text-sm font-medium ${
                              locationFilter === loc.value
                                ? 'border-orange-500 bg-orange-50 text-orange-700'
                                : 'border-gray-200 hover:border-gray-300 text-gray-600'
                            }`}
                          >
                            {loc.label}
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Exercises Grid */}
                <div className="mb-4 flex items-center justify-between">
                  <h3 className="font-bold text-gray-900">
                    {filteredExercises.length} exerciții găsite
                  </h3>
                  {(difficultyFilter !== 'toate' || locationFilter !== 'toate') && (
                    <button
                      onClick={() => { setDifficultyFilter('toate'); setLocationFilter('toate'); setWorkoutTypeFilter('toate'); }}
                      className="text-xs text-orange-600 hover:text-orange-700 font-medium flex items-center gap-1"
                    >
                      <X className="w-3 h-3" /> Resetează filtre
                    </button>
                  )}
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {filteredExercises.map((ex, idx) => (
                    <div
                      key={idx}
                      onClick={() => setSelectedVideo(ex)}
                      className="bg-white border-2 border-gray-100 rounded-2xl overflow-hidden hover:border-orange-400 hover:shadow-xl cursor-pointer transition-all group"
                    >
                      {/* Thumbnail */}
                      <div className="relative aspect-video bg-gray-100">
                        <img
                          src={`https://img.youtube.com/vi/${getYouTubeId(ex.youtube_url)}/mqdefault.jpg`}
                          alt={ex.title}
                          className="w-full h-full object-cover"
                        />
                        <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-all flex items-center justify-center">
                          <div className="w-12 h-12 bg-white/90 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all shadow-lg">
                            <Play className="w-6 h-6 text-orange-600 ml-0.5" />
                          </div>
                        </div>
                        {/* Difficulty badge */}
                        <div className="absolute top-2 right-2">
                          <span className={`text-xs px-2 py-1 rounded-full border font-medium ${getDifficultyConfig(ex.difficulty).class}`}>
                            {getDifficultyConfig(ex.difficulty).label}
                          </span>
                        </div>
                      </div>

                      {/* Card body */}
                      <div className="p-4">
                        <h4 className="font-semibold text-gray-900 mb-2 line-clamp-2 group-hover:text-orange-600 transition-colors text-sm">
                          {ex.title}
                        </h4>

                        <div className="flex items-center gap-2 mb-3 flex-wrap">
                          <span className={`text-xs px-2 py-1 rounded-full font-medium ${getLocationConfig(ex.location).color}`}>
                            {getLocationConfig(ex.location).icon} {getLocationConfig(ex.location).label}
                          </span>
                          <span className="text-xs text-gray-500 flex items-center gap-1">
                            <Clock className="w-3 h-3" />
                            {ex.duration} min
                          </span>
                        </div>

                        <p className="text-xs text-gray-600 line-clamp-2 flex items-start gap-1">
                          <Zap className="w-3 h-3 text-orange-500 flex-shrink-0 mt-0.5" />
                          {ex.benefits}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>

                {filteredExercises.length === 0 && (
                  <div className="text-center py-16 bg-white rounded-2xl shadow-md">
                    <Filter className="w-14 h-14 text-gray-200 mx-auto mb-4" />
                    <p className="text-gray-500 font-medium">Niciun exercițiu cu filtrele selectate</p>
                    <button
                      onClick={() => { setDifficultyFilter('toate'); setLocationFilter('toate'); }}
                      className="mt-3 text-sm text-orange-600 hover:text-orange-700 font-medium"
                    >
                      Resetează filtrele
                    </button>
                  </div>
                )}
              </div>
            )}
          </>
        )}

        {/* EMPTY STATE */}
        {exercises.length === 0 && !error && !loading && (
          <div className="bg-white rounded-3xl shadow-xl overflow-hidden">
            {/* Hero section */}
            <div className="bg-gradient-to-br from-orange-500 to-red-500 p-10 text-white text-center">
              <div className="w-24 h-24 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-5 backdrop-blur-sm">
                <Dumbbell className="w-12 h-12 text-white" />
              </div>
              <h3 className="text-3xl font-bold mb-3">
                Antrenamentul tău personalizat
              </h3>
              <p className="text-white/85 max-w-md mx-auto text-lg">
                AI-ul nostru fitness creează un program de exerciții adaptat perfect obiectivului și profilului tău
              </p>
            </div>

            {/* Features */}
            <div className="p-8">
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8">
                <div className="bg-orange-50 rounded-2xl p-5 text-center border border-orange-100">
                  <div className="text-3xl mb-2">🎥</div>
                  <p className="font-bold text-gray-900 mb-1">15+ Video-uri</p>
                  <p className="text-xs text-gray-600">Exerciții video reale de pe YouTube</p>
                </div>
                <div className="bg-red-50 rounded-2xl p-5 text-center border border-red-100">
                  <div className="text-3xl mb-2">🎯</div>
                  <p className="font-bold text-gray-900 mb-1">100% Personalizat</p>
                  <p className="text-xs text-gray-600">Adaptat obiectivului tău fitness</p>
                </div>
                <div className="bg-yellow-50 rounded-2xl p-5 text-center border border-yellow-100">
                  <div className="text-3xl mb-2">📅</div>
                  <p className="font-bold text-gray-900 mb-1">Plan Zilnic</p>
                  <p className="text-xs text-gray-600">Recomandări diferite în fiecare zi</p>
                </div>
              </div>

              {/* What you get */}
              <div className="bg-gray-50 rounded-2xl p-5 mb-8">
                <h4 className="font-bold text-gray-900 mb-3 flex items-center gap-2">
                  <BarChart2 className="w-5 h-5 text-orange-500" />
                  Ce primești:
                </h4>
                <div className="space-y-2">
                  {[
                    '✅ Exerciții ușoare, medii și avansate',
                    '✅ Video-uri YouTube cu demonstrații reale',
                    '✅ Beneficii și mușchi targetați pentru fiecare exercițiu',
                    '✅ Antrenament zilnic recomandat (rotație automată)',
                    '✅ Filtrare după dificultate și locație'
                  ].map((item, i) => (
                    <p key={i} className="text-sm text-gray-700">{item}</p>
                  ))}
                </div>
              </div>

              <div className="text-center">
                <button
                  onClick={generateExercises}
                  disabled={generating}
                  className="inline-flex items-center gap-3 px-10 py-4 bg-gradient-to-r from-orange-500 to-red-500 text-white rounded-2xl hover:shadow-xl transition-all disabled:opacity-50 text-lg font-bold shadow-lg"
                >
                  <Sparkles className="w-6 h-6" />
                  <span>{generating ? 'Generez antrenamentul...' : 'Generează Antrenamentul Meu'}</span>
                </button>
                <p className="text-xs text-gray-500 mt-3">
                  ⏱️ Durată generare: 2-3 minute • 🤖 Powered by FII FIT AI
                </p>
                {!user?.healthGoal && (
                  <p className="text-xs text-orange-600 mt-2 font-medium">
                    💡 Setează obiectivul fitness în profil pentru rezultate mai bune
                  </p>
                )}
              </div>
            </div>
          </div>
        )}

        {/* FITNESS DISCLAIMER */}
        <div className="bg-blue-50 border border-blue-200 rounded-2xl p-4 mt-6">
          <p className="text-xs text-blue-800 leading-relaxed">
            <strong>ℹ️ Informație:</strong> Exercițiile sunt generate de <strong>FII FIT AI</strong> și personalizate în funcție de obiectivul tău fitness. <strong>Consultați un antrenor personal certificat</strong> înainte de a începe un program nou, mai ales dacă aveți limitări fizice. Opriți exercițiul dacă simțiți durere. Surse: <a href="https://www.acsm.org" target="_blank" rel="noopener noreferrer" className="underline">ACSM</a>, <a href="https://www.who.int/news-room/fact-sheets/detail/physical-activity" target="_blank" rel="noopener noreferrer" className="underline">OMS</a>, <a href="https://www.nsca.com" target="_blank" rel="noopener noreferrer" className="underline">NSCA</a>.
          </p>
        </div>

        {/* Progress Modal */}
        <ExerciseProgressModal
          isOpen={showProgress}
          onComplete={handleProgressComplete}
          onError={handleProgressError}
          onDismiss={() => {
            setShowProgress(false);
            setGenerating(false);
          }}
        />

        {/* Video Modal */}
        {selectedVideo && (
          <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4 backdrop-blur-sm">
            <div className="bg-white rounded-3xl max-w-4xl w-full max-h-[92vh] overflow-y-auto shadow-2xl">
              <div className="p-6">
                {/* Header */}
                <div className="flex items-start justify-between mb-4">
                  <h3 className="text-xl font-bold text-gray-900 pr-8 leading-tight">
                    {selectedVideo.title}
                  </h3>
                  <button
                    onClick={() => setSelectedVideo(null)}
                    className="p-2 hover:bg-gray-100 rounded-xl transition-colors flex-shrink-0"
                  >
                    <X className="w-6 h-6 text-gray-600" />
                  </button>
                </div>

                {/* Badges */}
                <div className="flex flex-wrap gap-2 mb-4">
                  <span className={`px-3 py-1.5 rounded-full border text-sm font-medium ${getDifficultyConfig(selectedVideo.difficulty).class}`}>
                    {getDifficultyConfig(selectedVideo.difficulty).label}
                  </span>
                  <span className={`px-3 py-1.5 rounded-full text-sm font-medium ${getLocationConfig(selectedVideo.location).color}`}>
                    {getLocationConfig(selectedVideo.location).icon} {getLocationConfig(selectedVideo.location).label}
                  </span>
                  <span className="flex items-center gap-1.5 text-sm text-gray-600 bg-gray-100 px-3 py-1.5 rounded-full">
                    <Clock className="w-4 h-4" />
                    {selectedVideo.duration} minute
                  </span>
                </div>

                {/* YouTube Video */}
                <div className="mb-5 rounded-2xl overflow-hidden">
                  <YouTubePlayer
                    url={selectedVideo.youtube_url}
                    title={selectedVideo.title}
                    autoplay={true}
                  />
                </div>

                {/* Details */}
                <div className="space-y-4">
                  <div className="bg-orange-50 rounded-2xl p-4 border border-orange-100">
                    <h4 className="font-bold text-gray-900 mb-2 flex items-center gap-2">
                      <Zap className="w-5 h-5 text-orange-500" />
                      Beneficii & Mușchi Activați
                    </h4>
                    <p className="text-gray-700 text-sm leading-relaxed">{selectedVideo.benefits}</p>
                  </div>

                  {selectedVideo.muscle_groups && (
                    <div className="bg-blue-50 rounded-2xl p-4 border border-blue-100">
                      <h4 className="font-bold text-gray-900 mb-2 flex items-center gap-2">
                        <Activity className="w-5 h-5 text-blue-500" />
                        Grupe Musculare
                      </h4>
                      <p className="text-gray-700 text-sm">{selectedVideo.muscle_groups}</p>
                    </div>
                  )}

                  {selectedVideo.contraindications && (
                    <div className="bg-yellow-50 border border-yellow-200 rounded-2xl p-4">
                      <h4 className="font-semibold text-yellow-900 mb-2 flex items-center gap-2">
                        <AlertCircle className="w-5 h-5 text-yellow-600" />
                        Precauții
                      </h4>
                      <p className="text-yellow-800 text-sm">{selectedVideo.contraindications}</p>
                    </div>
                  )}

                  {/* Tips */}
                  <div className="bg-green-50 rounded-2xl p-4 border border-green-100">
                    <h4 className="font-bold text-gray-900 mb-2 flex items-center gap-2">
                      <TrendingUp className="w-5 h-5 text-green-500" />
                      Sfaturi de Performanță
                    </h4>
                    <ul className="space-y-1 text-sm text-gray-700">
                      <li>• Încălzește-te 5 minute înainte de antrenament</li>
                      <li>• Menține forma corectă pe tot parcursul exercițiului</li>
                      <li>• Hidratează-te corespunzător (500ml apă/oră de antrenament)</li>
                      <li>• Odihnă 48h între antrenamentele pentru același grup muscular</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* AI Consent Modal */}
      <AIConsentModal
        isOpen={showAIConsent}
        onAccept={() => setShowAIConsent(false)}
        onDecline={() => navigate('/dashboard')}
      />

      {/* Insufficient Credits Modal */}
      <InsufficientCreditsModal
        isOpen={creditsModal.open}
        available={creditsModal.available}
        required={creditsModal.required}
        onClose={() => setCreditsModal({ open: false, available: 0, required: 0 })}
      />
    </div>
  );
}
